import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { filter } from 'rxjs';
import { PastDispatchTableRow } from '../../../models/performance-view.model';
import { ProgramOrganizationType } from '../../../models/program-organization.model';
import { selectFilters, selectProductPerformanceEvents, selectProducts, selectSitePerformanceEvents, selectViewMode } from '../../../store/selectors/pd.selectors';
import { RoutesName } from '../../shared/constants/routes-name';
import { productPerformanceEventToPastDispatchTableRow, toPastDispatchTableRow } from '../../shared/helpers/performance-view.mapper';
import { DispatchPortfolioComponent } from '../dispatch-portfolio/dispatch-portfolio.component';
import { PerformanceOverviewComponent } from '../performance-overview/performance-overview.component';
import { PastDispatchesFiltersComponent } from './past-dispatches-filters/past-dispatches-filters.component';
import { PerformanceDetailsComponent } from './performance-details/performance-details.component';
import { PerformanceIntervalComponent } from './performance-interval/performance-interval.component';

@Component({
  selector: 'fdr-past-performance-ui-past-dispatches',
  imports: [
    DispatchPortfolioComponent,
    PastDispatchesFiltersComponent,
    PerformanceDetailsComponent,
    PerformanceOverviewComponent,
    PerformanceIntervalComponent,
  ],
  templateUrl: './past-dispatches.component.html',
  styleUrl: './past-dispatches.component.scss',
})
export class PastDispatchesComponent implements OnInit {
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly destroyRef = inject(DestroyRef);
  readonly store = inject(Store);

  activeRouteName = signal<RoutesName>(RoutesName.ROOT);
  routesName = RoutesName;

  private readonly viewMode = this.store.selectSignal(selectViewMode);
  private readonly filters = this.store.selectSignal(selectFilters);
  private readonly performanceEventDetails = this.store.selectSignal(selectSitePerformanceEvents);
  private readonly productPerformanceEvents = this.store.selectSignal(selectProductPerformanceEvents);
  private readonly products = this.store.selectSignal(selectProducts);

  readonly rowsSignal = computed(() => {
    const isProgram = this.viewMode() === ProgramOrganizationType.Program;
    const isPortfolioView = this.activeRouteName() === RoutesName.DISPATCH_PORTFOLIO;
    const rows = isProgram ? this.buildProgramRows() : this.buildSiteRows();

    return [...rows].sort((a, b) => {
      const dateDiff = moment.utc(b.eventStartDateUtc).valueOf() - moment.utc(a.eventStartDateUtc).valueOf(); // sort by date

      if (dateDiff !== 0) return dateDiff;
      return isPortfolioView ? a.portfolio.localeCompare(b.portfolio) : a.site.localeCompare(b.site); // if same date sort by portfolio/site name
    });
  });

  private buildProgramRows(): PastDispatchTableRow[] {
    const productData = this.productPerformanceEvents();
    if (!productData?.length) return [];

    const productNameById = new Map(this.products().map(p => [p.id, p.name]));

    return productData.map(e => {
      const row = productPerformanceEventToPastDispatchTableRow(e);
      const name = productNameById.get(e.productId) ?? row.product;
      row.product = name;
      return row;
    });
  }

  private buildSiteRows(): PastDispatchTableRow[] {
    const siteData = this.performanceEventDetails();
    return siteData?.length ? siteData.map(toPastDispatchTableRow) : [];
  }

  constructor() {}

  ngOnInit(): void {
    this.setActiveRouteName();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setActiveRouteName();
      });
  }

  private setActiveRouteName() {
    // Get the deepest active child route (where params actually exist)
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const params = route.snapshot.params;
    const path = route.snapshot.routeConfig?.path;

    if (path === RoutesName.EVENT_DETAILS) {
      this.activeRouteName.set(RoutesName.EVENT_DETAILS);
    } else if (params['productId']) {
      this.activeRouteName.set(RoutesName.DISPATCH_PORTFOLIO);
    } else if (params['siteId']) {
      this.activeRouteName.set(RoutesName.DISPATCH_SITE);
    } else {
      this.activeRouteName.set(RoutesName.ROOT);
    }
  }
}
